import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import { StudyEditFormProps } from '@/features/studies/types/study-edit.interface';
import { EmptyQuestionnaireCard, QuestionnaireCard } from './QuestionnaireCard';
import { useStudyFormContext } from '../contexts';
import { useFieldArray } from 'react-hook-form';
import { getQuestionnaireErrors } from '../utils/questionnaireErrors';

type Props = Pick<StudyEditFormProps, 'formLoading' | 'studyId' | 'control'>;

export const EmaQuestionnaireSection = ({
  formLoading,
  studyId,
  control,
}: Props) => {
  const { t } = useTranslation();
  const { saveAndNavigate } = useStudyFormContext();

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6" gutterBottom>
          {t('pages.studies.sections.emaQuestionnaires')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            saveAndNavigate(`/studies/${studyId}/ema-questionnaire/create`);
          }}
          disabled={formLoading}
          size="small"
        >
          {t('buttons.createEmaQuestionnaire')}
        </Button>
      </Box>
      <EmaQuestionnaireContent
        control={control}
        formLoading={formLoading}
        studyId={studyId}
      />
    </Box>
  );
};

const EmaQuestionnaireContent = ({ control, formLoading, studyId }: Props) => {
  const { t } = useTranslation();
  const {
    saveAndNavigate,
    formState: { errors },
  } = useStudyFormContext();
  const { fields: questionnaires, remove } = useFieldArray({
    control,
    name: 'emaQuestionnaires',
    keyName: '_id',
  });
  const handleNavigateToCreateEmaQuestionnaire = () => {
    saveAndNavigate(`/studies/${studyId}/ema-questionnaire/create`);
  };

  const handleDeleteQuestionnaire = (index: number) => {
    remove(index);
  };

  const handleEditQuestionnaire = (questionnaireId: string) => {
    saveAndNavigate(
      `/studies/${studyId}/ema-questionnaire/edit/${questionnaireId}`,
    );
  };

  if (questionnaires.length === 0) {
    return (
      <EmptyQuestionnaireCard
        onCreate={handleNavigateToCreateEmaQuestionnaire}
        formLoading={formLoading}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {questionnaires.map((questionnaire, index) => {
        const questionnaireErrors = getQuestionnaireErrors(errors, index);
        return (
          <QuestionnaireCard
            key={questionnaire.id}
            questionnaire={questionnaire}
            title={t('pages.studies.emaQuestionnaire.item.title', {
              number: index + 1,
            })}
            onEdit={() => handleEditQuestionnaire(questionnaire.id)}
            onDelete={() => handleDeleteQuestionnaire(index)}
            formLoading={formLoading}
            errors={questionnaireErrors}
          />
        );
      })}
    </Box>
  );
};
